import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import * as React from 'react';

import { cn } from '../presets/utils';

const TooltipProvider = TooltipPrimitive.Provider;

/**
 * Shared open state between Root and Trigger so the Trigger can flip
 * the tooltip on a touch tap (Radix Tooltip is hover-only by default,
 * which leaves POS / tablet users unable to surface the help text).
 */
type TooltipCtx = {
  isControlled: boolean;
  open: boolean;
  setOpen: (next: boolean) => void;
};
const TooltipContext = React.createContext<TooltipCtx | null>(null);

type TooltipRootProps = React.ComponentPropsWithoutRef<
  typeof TooltipPrimitive.Root
>;

/**
 * Drop-in replacement for Radix Tooltip.Root that:
 *   - keeps the original hover behavior on devices with a mouse,
 *   - adds a tap-to-toggle path on touch so the same component works
 *     on POS tablets without any per-screen wiring.
 *
 * The Trigger we export below picks up the shared context to flip the
 * open state on `pointerType === 'touch' | 'pen'`.
 */
const Tooltip = ({
  open: openProp,
  defaultOpen,
  onOpenChange,
  ...rest
}: TooltipRootProps) => {
  const isControlled = openProp !== undefined;
  const [internalOpen, setInternalOpen] = React.useState(!!defaultOpen);
  const open = isControlled ? !!openProp : internalOpen;
  const setOpen = React.useCallback(
    (next: boolean) => {
      if (!isControlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange]
  );
  const ctx = React.useMemo(
    () => ({ isControlled, open, setOpen }),
    [isControlled, open, setOpen]
  );
  return (
    <TooltipContext.Provider value={ctx}>
      <TooltipPrimitive.Root open={open} onOpenChange={setOpen} {...rest} />
    </TooltipContext.Provider>
  );
};

type TooltipTriggerProps = React.ComponentPropsWithoutRef<
  typeof TooltipPrimitive.Trigger
>;

const TooltipTrigger = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Trigger>,
  TooltipTriggerProps
>(({ onPointerDown, onClick, ...props }, ref) => {
  const ctx = React.useContext(TooltipContext);
  return (
    <TooltipPrimitive.Trigger
      ref={ref}
      onPointerDown={(event) => {
        // On touch / stylus the hover path never fires — toggle the
        // tooltip explicitly so cashiers on a POS tablet can read it.
        if (
          ctx &&
          (event.pointerType === 'touch' || event.pointerType === 'pen')
        ) {
          ctx.setOpen(!ctx.open);
        }
        onPointerDown?.(event);
      }}
      onClick={(event) => {
        onClick?.(event);
      }}
      {...props}
    />
  );
});
TooltipTrigger.displayName = TooltipPrimitive.Trigger.displayName;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        'z-50 overflow-hidden rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-tooltip-content-transform-origin]',
        className
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger };
